import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable, Subject, Subscription } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

import { ApiConfiguration } from '../../api/api-configuration';
import { ClientInvoiceExportRequest } from '../../api/models/client-invoice-export-request';
import { ClientInvoiceExportScope } from '../../api/models/client-invoice-export-scope';
import { ClientInvoiceExportSummary } from '../../api/models/client-invoice-export-summary';
import { ExportChangedEvent } from '../../api/models/export-changed-event';
import { ExportStatus } from '../../api/models/export-status';
import { RealtimeEvent, RealtimeNotificationsService } from '../../service/realtime-notifications.service';
import { ClientExportsModalComponent, ClientExportsModalData } from './client-exports-modal/client-exports-modal.component';
import { ClientExportsService } from './client-exports.service';

@Component({
  selector: 'app-client-exports-widget',
  templateUrl: './client-exports-widget.component.html',
  styleUrls: ['./client-exports-widget.component.scss'],
  providers: [ClientExportsService],
})
export class ClientExportsWidgetComponent implements OnInit, OnChanges, OnDestroy {
  private readonly exportsService = inject(ClientExportsService);
  private readonly realtime = inject(RealtimeNotificationsService);
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);
  private readonly apiConfig = inject(ApiConfiguration);

  @Input() public clientId: number | null = null;
  @Input() public clientName = '';

  public exports$: Observable<ClientInvoiceExportSummary[]>;
  public submitting = false;

  public readonly displayedColumns = [
    'requested_at',
    'scope',
    'status',
    'size',
    'expires_at',
    'actions',
  ];

  private readonly ngDestroy$ = new Subject<void>();
  private scopeSub: Subscription | null = null;
  private currentClientId: number | null = null;

  public ngOnInit(): void {
    this.exports$ = this.exportsService.exports$;
    this.realtime.events$
      .pipe(takeUntil(this.ngDestroy$))
      .subscribe((event) => this.handleEvent(event));
    this.refreshScope();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['clientId'] && !changes['clientId'].firstChange) {
      this.refreshScope();
    }
  }

  public ngOnDestroy(): void {
    this.scopeSub?.unsubscribe();
    this.scopeSub = null;
    this.ngDestroy$.next();
    this.ngDestroy$.complete();
  }

  public openExportDialog(): void {
    if (this.clientId == null) {
      return;
    }
    const data: ClientExportsModalData = {
      clientId: this.clientId,
      clientName: this.clientName,
    };
    const dialogRef = this.dialog.open<ClientExportsModalComponent, ClientExportsModalData, ClientInvoiceExportRequest | undefined>(
      ClientExportsModalComponent,
      { width: '520px', data },
    );
    dialogRef.afterClosed().subscribe((request) => {
      if (request) {
        this.submit(request);
      }
    });
  }

  public download(row: ClientInvoiceExportSummary): void {
    if (!this.canDownload(row)) {
      return;
    }
    const url = `${this.apiConfig.rootUrl}/exports/${row.export_id}/download`;
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  public canDownload(row: ClientInvoiceExportSummary): boolean {
    return row.status === ExportStatus.Ready;
  }

  public scopeSummary(row: ClientInvoiceExportSummary): string {
    if (!row.parameters?.scopes || row.parameters.scopes.length === 0) {
      return '—';
    }
    return row.parameters.scopes.map(scopeShortLabel).join(', ');
  }

  public statusLabel(row: ClientInvoiceExportSummary): string {
    switch (row.status) {
      case ExportStatus.Pending:
        return 'Pending';
      case ExportStatus.Running:
        return 'Running';
      case ExportStatus.Ready:
        return 'Ready';
      case ExportStatus.Failed:
        return 'Failed';
      case ExportStatus.Expired:
        return 'Expired';
      default:
        return row.status;
    }
  }

  public formatSize(size: number | null | undefined): string {
    if (size == null) {
      return '';
    }
    if (size < 1024) {
      return `${size} B`;
    }
    if (size < 1024 * 1024) {
      return `${(size / 1024).toFixed(1)} KB`;
    }
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  }

  private submit(request: ClientInvoiceExportRequest): void {
    if (this.clientId == null) {
      return;
    }
    this.submitting = true;
    // finalize: HttpErrorInterceptor completes a 401, a 403 or an aborted
    // request without a value or an error, which would leave submitting set.
    this.exportsService.submit(this.clientId, request).pipe(finalize(() => (this.submitting = false))).subscribe({
      next: () => {
        this.submitting = false;
        // The pending row arrives via the SSE export_changed event; no
        // immediate list patch needed here.
        this.snackBar.open('Export started.', 'OK', { duration: 3000 });
      },
      error: (err) => {
        this.submitting = false;
        console.error('failed to start export', err);
        this.snackBar.open('Failed to start export.', 'OK', { duration: 5000 });
      },
    });
  }

  private refreshScope(): void {
    if (this.currentClientId === this.clientId) {
      return;
    }
    this.scopeSub?.unsubscribe();
    this.scopeSub = null;
    this.currentClientId = this.clientId;
    if (this.clientId == null || this.clientId <= 0) {
      return;
    }
    this.scopeSub = this.realtime.subscribeToClient(this.clientId);
    this.exportsService.seed(this.clientId).subscribe({
      error: (err) => console.error('failed to seed exports', err),
    });
  }

  private handleEvent(event: RealtimeEvent): void {
    if (event.event_type !== 'export_changed') {
      return;
    }
    const exportEvent = event as ExportChangedEvent;
    if (exportEvent.client_id !== this.clientId) {
      return;
    }
    this.exportsService.applyEvent(exportEvent);
  }
}

function scopeShortLabel(scope: ClientInvoiceExportScope): string {
  switch (scope) {
    case ClientInvoiceExportScope.OriginalInvoice:
      return 'Originals';
    case ClientInvoiceExportScope.OriginalInvoiceAttachment:
      return 'Attachments';
    case ClientInvoiceExportScope.Invoice:
      return 'Invoices';
    default:
      return scope;
  }
}
